import type {
  CorvidSDKPropsFactory,
  ICorvidEvent,
  ICorvidEventHandler,
  IComponentEvent,
} from '@wix/editor-elements-types/corvid';
import {
  toJSONBase,
  withValidation,
  registerCorvidEvent,
} from '@wix/editor-elements-corvid-utils';
import type {
  ISearchBoxSDK,
  ISearchBoxProps,
  SearchSuggestionGroup,
  SearchSuggestionItem,
  SearchSuggestions,
} from '../SearchBox.types';

export interface ISearchBoxValuePropsSDKProps {
  autocompleteValue?: string;
  closeParentContainer?: boolean;
  isSuggestionsOpen?: boolean;
  suggestions?: SearchSuggestions;
  suggestionsEnabled?: boolean;
  suggestionsLoading?: boolean;
  trendingItemsEnabled: boolean;
  value?: string | null;
}

interface SuggestionSelectEventPayload {
  syntheticEvent: SearchSuggestionItem | SearchSuggestionGroup;
}

interface AutocompleteEventPayload {
  previousValue: string;
}

interface ClearEventPayload {
  previousValue: string;
}

interface ChangeEventPayload {
  previousValue: string;
}

type SubmitCorvidEvent = ICorvidEvent<'submit'>;
type ChangeCorvidEvent = ICorvidEvent<'change', ChangeEventPayload>;
type ClearCorvidEvent = ICorvidEvent<'clear', ClearEventPayload>;
type SuggestionSelectCorvidEvent = ICorvidEvent<
  'suggestionSelect',
  SuggestionSelectEventPayload
>;
type SuggestionGroupSelectCorvidEvent = ICorvidEvent<
  'suggestionGroupSelect',
  SuggestionSelectEventPayload
>;
type SuggestionsFooterClickCorvidEvent = ICorvidEvent<'suggestionsFooterClick'>;
type AutocompleteCorvidEvent = ICorvidEvent<
  'autocomplete',
  AutocompleteEventPayload
>;

export interface ISearchBoxValuePropsSDK {
  autocompleteEnabled: boolean;
  autocompleteValue: string;
  closeSuggestions(): void;
  isSuggestionsOpen?: boolean;
  openSuggestions(): void;
  suggestions: SearchSuggestions;
  suggestionsEnabled: boolean;
  suggestionsLoading: boolean;
  trendingItemsEnabled: boolean;
  value?: string | null;
  onAutocomplete(
    handler: ICorvidEventHandler<AutocompleteCorvidEvent>,
  ): ISearchBoxSDK;
  onSubmit(handler: ICorvidEventHandler<SubmitCorvidEvent>): ISearchBoxSDK;
  onClear(handler: ICorvidEventHandler<ClearCorvidEvent>): ISearchBoxSDK;
  onChange(handler: ICorvidEventHandler<ChangeCorvidEvent>): ISearchBoxSDK;
  onSuggestionsFooterClick(
    handler: ICorvidEventHandler<SuggestionsFooterClickCorvidEvent>,
  ): ISearchBoxSDK;
  onSuggestionSelect(
    handler: ICorvidEventHandler<SuggestionSelectCorvidEvent>,
  ): ISearchBoxSDK;
  onSuggestionGroupSelect(
    handler: ICorvidEventHandler<SuggestionGroupSelectCorvidEvent>,
  ): ISearchBoxSDK;
}

type SubmitEvent = IComponentEvent<'submit'>;
type ClearEvent = IComponentEvent<'clear', ClearEventPayload>;
type ChangeEvent = IComponentEvent<'change', ChangeEventPayload>;
type SuggestionsFooterClickEvent = IComponentEvent<'suggestionsFooterClick'>;
type SuggestionSelectEvent = IComponentEvent<
  'suggestionSelect',
  SuggestionSelectEventPayload
>;
type SuggestionGroupSelectEvent = IComponentEvent<
  'suggestionGroupSelect',
  SuggestionSelectEventPayload
>;
type AutocompleteEvent = IComponentEvent<
  'autocomplete',
  AutocompleteEventPayload
>;

export interface ISearchBoxValuePropsSDKActions {
  onSubmit(event: SubmitEvent): void;
  onClear(event: ClearEvent): void;
  onChange(event: ChangeEvent): void;
  onSuggestionsFooterClick(event: SuggestionsFooterClickEvent): void;
  onSuggestionSelect(event: SuggestionSelectEvent): void;
  onSuggestionGroupSelect(event: SuggestionGroupSelectEvent): void;
  onAutocomplete(event: AutocompleteEvent): void;
}

const emptySearchSuggestions = null;

const _searchBoxValuePropsSDKFactory: CorvidSDKPropsFactory<
  ISearchBoxProps,
  ISearchBoxValuePropsSDK
> = api => {
  const { setProps, props, metaData } = api;

  return {
    get value() {
      return props.value || '';
    },
    set value(_value) {
      const value = _value || '';
      setProps({ value });
    },
    get autocompleteEnabled() {
      return props.autocompleteEnabled;
    },
    set autocompleteEnabled(autocompleteEnabled) {
      setProps({ autocompleteEnabled });
    },
    get autocompleteValue() {
      return props.autocompleteValue || '';
    },
    set autocompleteValue(autocompleteValue) {
      setProps({ autocompleteValue });
    },
    get suggestions() {
      return props.suggestions || emptySearchSuggestions;
    },
    set suggestions(suggestions) {
      const hasSuggestions = Boolean(suggestions?.items.length);

      // On desktop we close suggestions when empty suggestion list is received;
      // On mobile we want to keep suggestions open until user click on close suggestions button.
      setProps({
        isSuggestionsOpen: props.useModalSuggestions
          ? props.isSuggestionsOpen
          : hasSuggestions,
        suggestions,
      });
    },
    get suggestionsEnabled() {
      return props.suggestionsEnabled || false;
    },
    set suggestionsEnabled(suggestionsEnabled) {
      setProps({ suggestionsEnabled });
    },
    get trendingItemsEnabled() {
      return props.trendingItemsEnabled;
    },
    set trendingItemsEnabled(trendingItemsEnabled) {
      setProps({ trendingItemsEnabled });
    },
    get suggestionsLoading() {
      return props.suggestionsLoading || false;
    },
    set suggestionsLoading(suggestionsLoading) {
      setProps({ suggestionsLoading });
    },
    openSuggestions: () => {
      setProps({
        isSuggestionsOpen: true,
        closeParentContainer: false,
      });
    },
    closeSuggestions: () => {
      setProps({
        isSuggestionsOpen: false,
        closeParentContainer: true,
      });
    },
    onSubmit(handler) {
      return registerCorvidEvent('onSubmit', api, handler);
    },
    onClear(handler) {
      return registerCorvidEvent<ClearEvent, ClearCorvidEvent>(
        'onClear',
        api,
        handler,
        ({ componentEvent: { previousValue } }) => ({ previousValue }),
      );
    },
    onChange(handler) {
      return registerCorvidEvent<ChangeEvent, ChangeCorvidEvent>(
        'onChange',
        api,
        handler,
        ({ componentEvent: { previousValue } }) => ({ previousValue }),
      );
    },
    onSuggestionsFooterClick(handler) {
      return registerCorvidEvent('onSuggestionsFooterClick', api, handler);
    },
    onSuggestionSelect(handler) {
      return registerCorvidEvent<
        SuggestionSelectEvent,
        SuggestionSelectCorvidEvent
      >(
        'onSuggestionSelect',
        api,
        handler,
        ({ componentEvent: { syntheticEvent } }) => ({ syntheticEvent }),
      );
    },
    onSuggestionGroupSelect(handler) {
      return registerCorvidEvent<
        SuggestionGroupSelectEvent,
        SuggestionGroupSelectCorvidEvent
      >(
        'onSuggestionGroupSelect',
        api,
        handler,
        ({ componentEvent: { syntheticEvent } }) => ({ syntheticEvent }),
      );
    },
    onAutocomplete(handler) {
      return registerCorvidEvent<AutocompleteEvent, AutocompleteCorvidEvent>(
        'onAutocomplete',
        api,
        handler,
        ({ componentEvent: { previousValue } }) => ({ previousValue }),
      );
    },
    toJSON: () => {
      const {
        autocompleteEnabled,
        autocompleteValue = '',
        placeholder = '',
        clearButtonText = 'clear',
        suggestions = emptySearchSuggestions,
        suggestionsEnabled = false,
        suggestionsLoading = false,
        trendingItemsEnabled,
        value = '',
      } = props;
      return {
        ...toJSONBase(metaData),
        autocompleteEnabled,
        autocompleteValue,
        placeholder,
        clearButtonText,
        suggestions,
        suggestionsEnabled,
        suggestionsLoading,
        trendingItemsEnabled,
        type: '$w.SearchBox',
        value,
      };
    },
  };
};

export const searchBoxValuePropsSDKFactory = withValidation(
  _searchBoxValuePropsSDKFactory,
  {
    type: ['object'],
    properties: {
      label: {
        type: ['string', 'nil'],
        warnIfNil: true,
      },
      suggestionsEnabled: { type: ['boolean'] },
      autocompleteEnabled: { type: ['boolean'] },
      autocompleteValue: { type: ['string'] },
      suggestions: { type: ['object', 'nil'] },
      suggestionsLoading: { type: ['boolean'] },
      trendingItemsEnabled: { type: ['boolean'] },
    },
  },
);
